import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { apiErr } from "../lib/api";
import { HandHeart, HandCoins, Eye, Check, Loader2, ArrowRight } from "lucide-react";

const OPTIONS = [
  { key: "prayer", label: "I'll pray", desc: "Lift up the mission and the people we meet.", icon: HandHeart },
  { key: "financial", label: "I'll give", desc: "Help carry the journey financially.", icon: HandCoins },
  { key: "follow", label: "I'll follow & encourage", desc: "Cheer us on and stay in the story.", icon: Eye },
];

// Shared "How would you like to walk with us?" picker.
// Used at public sign-up (dark) and as a one-time card for existing partners (light).
export default function IntentPicker({ initial = [], onDone, dark = false, gold = "#E0A73A" }) {
  const [sel, setSel] = useState(initial);
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const save = async () => {
    if (sel.length === 0) return toast.error("Choose at least one — even just following means a lot.");
    setSaving(true);
    try {
      const { data } = await api.put("/me/partnership-ways", { partnership_ways: sel });
      onDone?.(data.partnership_ways || sel);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="intent-picker">
      {OPTIONS.map((o) => {
        const active = sel.includes(o.key);
        const Icon = o.icon;
        return (
          <motion.button
            key={o.key}
            type="button"
            data-testid={`intent-opt-${o.key}`}
            aria-pressed={active}
            whileTap={{ scale: 0.98 }}
            onClick={() => toggle(o.key)}
            className={`w-full text-left rounded-2xl border-2 p-3.5 flex items-center gap-3 transition-colors ${
              active
                ? "border-transparent"
                : dark ? "border-white/20 bg-white/5" : "border-muted bg-white"
            }`}
            style={active ? { background: gold, borderColor: gold } : {}}
          >
            <span className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${active ? "bg-white/25" : dark ? "bg-white/10" : "bg-[var(--brand-navy)]/8"}`}>
              <Icon className={`h-5 w-5 ${active ? "text-white" : dark ? "text-white" : "text-[var(--brand-navy)]"}`} />
            </span>
            <span className="flex-1 min-w-0">
              <span className={`block font-heading font-bold ${active ? "text-white" : dark ? "text-white" : "text-[#2C2C3E]"}`}>{o.label}</span>
              <span className={`block text-xs ${active ? "text-white/90" : dark ? "text-white/60" : "text-muted-foreground"}`}>{o.desc}</span>
            </span>
            <span className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-white bg-white/20" : dark ? "border-white/40" : "border-muted-foreground/30"}`}>
              {active && <Check className="h-3.5 w-3.5 text-white" />}
            </span>
          </motion.button>
        );
      })}
      <button
        type="button"
        data-testid="intent-save-btn"
        disabled={saving}
        onClick={save}
        className="w-full rounded-full h-12 font-semibold flex items-center justify-center gap-2 transition-transform active:scale-[0.99]"
        style={{ background: gold, color: "#0d2340" }}
      >
        {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
      </button>
    </div>
  );
}
